/*
 *  Copyright (c) 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import dayjs from "dayjs";
import { useGetOrgUnitWithChildrenQuery } from "~/generated/graphql";
import { SortDirection } from "~/components";
import { formatCostObject } from "~/components/CostBox";
import { useCurrency, useQueryState } from "~/hooks";
import { useBillingPeriod } from "~/services/customers";
import { filterByName } from "~/tools";
import { SortFilter } from "../../constants";
import {
  buildDataTree,
  getOrgUnitWithItsParents,
  mapOrgUnits,
  sortOrgUnits,
} from "../../utils";
import { SearchParams } from "../Lists";
import { getProgress, mapTags } from "../mappers";

export function useOrgUnitWithChildren(orgUnitId: string, year: string) {
  const currentMonth = dayjs().month();
  const { isCurrentBillingPeriod } = useBillingPeriod();
  const { currency } = useCurrency();
  const {
    state: {
      sortField = SortFilter.UnitName,
      sortDirection = SortDirection.Ascending,
      search = "",
    },
  } = useQueryState<SearchParams>();

  const { data, ...rest } = useGetOrgUnitWithChildrenQuery({
    variables: { id: orgUnitId, year },
    errorPolicy: "all",
  });
  const orgUnit = data?.orgUnitWithChildren;

  const tags = mapTags(orgUnit?.type?.name);

  const label = useMemo(
    () => orgUnit?.name || orgUnitId,
    [orgUnit?.name, orgUnitId]
  );

  const costs = formatCostObject({
    data: {
      currentMonth: orgUnit?.cost?.currentMonth,
      previousMonth: orgUnit?.cost?.previousMonth,
      budget: orgUnit?.budgetYearly.budgetByMonth,
      forecast: orgUnit?.cost?.forecast,
      cloudWasteCost: orgUnit?.cloudWasteAggregate?.cost,
      co2e: orgUnit?.co2e?.currentMonth,
    },
    currency,
    isCurrentBillingPeriod,
  });

  const { progress, isForecast } = getProgress({
    budget: costs.budget ?? 0,
    forecast: costs.forecast,
    currentCost: costs.currentMonth,
  });

  const isEmptyData = orgUnit == null;

  const orgUnitDetails = {
    id: orgUnit?.id ?? "",
    name: orgUnit?.name ?? "",
    description: orgUnit?.description ?? "",
    contact: orgUnit?.contact ?? {
      id: orgUnit?.contact?.id ?? "",
      name: orgUnit?.contact?.name ?? "",
    },
    type: {
      id: orgUnit?.type?.id ?? "",
      name: orgUnit?.type?.name ?? "",
    },
  };

  const orgUnitChildren = useMemo(() => {
    const orgUnits = orgUnit?.children ?? [];
    const filteredOrgUnitsWithParents = [
      ...new Set(
        filterByName(orgUnits, search?.toString() ?? "").flatMap((item) =>
          getOrgUnitWithItsParents(item, orgUnits)
        )
      ),
    ];
    const mappedOrgUnits = mapOrgUnits(
      filteredOrgUnitsWithParents,
      currentMonth
    ).map((item) => ({
      ...item,
      parentNid: item.parentNid === orgUnitId ? null : item.parentNid,
    }));
    const treeData = buildDataTree(mappedOrgUnits);

    return sortOrgUnits(treeData, sortField, sortDirection);
  }, [
    currentMonth,
    orgUnit?.children,
    orgUnitId,
    search,
    sortDirection,
    sortField,
  ]);

  return {
    data: {
      tags,
      progress,
      isForecast,
      label,
      costs,
      orgUnitDetails,
      businessContext: orgUnit?.businessContext,
      orgUnitChildren,
    },
    isEmptyData,
    ...rest,
  };
}
